import { useCallback } from "react";
import { useFeedsRequest, IBulkFeedPayload } from "../../apis/feeds/useFeedsRequest";
import { useAppHandlers } from "../app/useAppHandlers";
import { useFeedsActions } from "../../../stores/feeds/useFeedsActions";

export const useFeedsHandlers = () => {
  const { getFeeds, bulkAddConnectionsToFeed, createFeed, bulkRemoveConnectionsFromFeed, deleteFeed, renameFeed, getFeedConnections } = useFeedsRequest();
  const { showSuccessToast, showErrorToast, hideModal, setNotFoundUrlsModalHandler } = useAppHandlers();
  const { setFeedData, addFeed, updateFeed, deleteFeed: deleteFeedFromStore, setLoading } = useFeedsActions();

  const getFeedsHandler = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getFeeds();
      console.log('Feeds API result:', result);
      // API might return { feeds: [...] } or directly [...]
      const feeds = result?.feeds || result || [];
      const numConnections = result?.numConnections;
      const maxNumConnections = result?.maxNumConnections;
      
      // Use setFeedData to store all data including limits
      setFeedData(feeds, numConnections, maxNumConnections);
      
      // Return the full result object to include maxNumConnections, numConnections, etc.
      return result;
    } catch (error) {
      console.error("Error fetching feeds:", error);
      showErrorToast({ message: "Failed to fetch feeds" });
      return { feeds: [], count: 0 };
    } finally {
      setLoading(false);
    }
  }, [getFeeds, showErrorToast, setFeedData, setLoading]);

  const bulkAddConnectionsToFeedHandler = useCallback(
    async (connectionIds: string[], feedIds: string[], feedName?: string, publicIdentifiers?: string[]) => {
      try {
        const payload: IBulkFeedPayload = {
          feedIds,
        };
        
        if (connectionIds && connectionIds.length > 0) {
          payload.connectionIds = connectionIds;
        }
        
        if (publicIdentifiers && publicIdentifiers.length > 0) {
          payload.publicIdentifiers = publicIdentifiers;
        }
        
        if (feedName) {
          payload.feedName = feedName;
        }
        
        const result = await bulkAddConnectionsToFeed(payload, true);
        if (result) {
          console.log('result', result?.message);
          // const message = feedId
          //   ? `Successfully added ${connectionIds.length} contacts to feed`
          //   : `Successfully created new feed "${feedName}" with ${connectionIds.length} contacts`;
          if(result?.success) {
            // Check for notFoundIdentifiers and show modal if any exist (only when using publicIdentifiers)
            const hasNotFoundIdentifiers = publicIdentifiers && publicIdentifiers.length > 0 && result?.data?.notFoundIdentifiers && result.data.notFoundIdentifiers.length > 0;
            
            if (hasNotFoundIdentifiers) {
              setNotFoundUrlsModalHandler(result.data.notFoundIdentifiers);
            }
            
            showSuccessToast({ message: result?.message });
            // Refresh feeds data to update numConnections and maxNumConnections
            await getFeedsHandler();
            
            // Only hide modal if we're not showing the NotFoundUrlsModal
            if (!hasNotFoundIdentifiers) {
              hideModal();
            }
          } else {
            showErrorToast({ message: result?.message });
            hideModal();
          }
          return result;
        }
      } catch (error) {
        console.error("Error adding connections to feed:", error);
        showErrorToast({ message: "Failed to add contacts to feed" });
        return null;
      }
    },
    [bulkAddConnectionsToFeed, showSuccessToast, showErrorToast, hideModal, getFeedsHandler, setNotFoundUrlsModalHandler]
  );

  const createFeedHandler = useCallback(
    async (feedName: string, connectionIds?: string[], publicIdentifiers?: string[]) => {
      try {
        const result = await createFeed(feedName, connectionIds, publicIdentifiers);
        if (result?.success) {
          // Check for notFoundIdentifiers and show modal if any exist (only when using publicIdentifiers)
          const hasNotFoundIdentifiers = publicIdentifiers && publicIdentifiers.length > 0 && result?.data?.notFoundIdentifiers && result.data.notFoundIdentifiers.length > 0;
          
          if (hasNotFoundIdentifiers) {
            setNotFoundUrlsModalHandler(result.data.notFoundIdentifiers);
          }
          
          // Optimistically update the store with the new feed
          // Feed is located at result.data.feed
          if (result?.data?.feed) {
            addFeed(result.data.feed);
          }
          showSuccessToast({ message: result?.message });
          // Refresh feeds data to update numConnections and maxNumConnections
          await getFeedsHandler();
          
          // Only hide modal if we're not showing the NotFoundUrlsModal
          if (!hasNotFoundIdentifiers) {
            hideModal();
          }
          return result;
        } else {
          showErrorToast({ message: result?.message || "Failed to create feed" });
          hideModal();
          return null;
        }
      } catch (error) {
        console.error("Error creating feed:", error);
        showErrorToast({ message: "Failed to create feed" });
        return null;
      }
    },
    [createFeed, addFeed, showSuccessToast, showErrorToast, hideModal, getFeedsHandler, setNotFoundUrlsModalHandler]
  );

  const deleteFeedHandler = useCallback(async (feedId: string) => {
    try {
      const result = await deleteFeed(feedId);
      if (result?.success) {
        deleteFeedFromStore(feedId);
        showSuccessToast({ message: "Feed deleted successfully" });
        // Refresh feeds data to update numConnections and maxNumConnections
        await getFeedsHandler();
        return result;
      } else {
        showErrorToast({ message: result?.message || "Failed to delete feed" });
        return null;
      }
    } catch (error) {
      console.error("Error deleting feed:", error);
      showErrorToast({ message: "Failed to delete feed" });
      return null;
    }
  }, [deleteFeed, showSuccessToast, showErrorToast, deleteFeedFromStore, getFeedsHandler]);

  const renameFeedHandler = useCallback(async (feedId: string, newName: string) => {
    try {
      const result = await renameFeed(feedId, newName);
      if (result?.success) {
        updateFeed(feedId, { name: newName });
        showSuccessToast({ message: "Feed renamed successfully" });
        return result;
      } else {
        showErrorToast({ message: result?.message || "Failed to rename feed" });
        return null;
      }
    } catch (error) {
      console.error("Error renaming feed:", error);
      showErrorToast({ message: "Failed to rename feed" });
      return null;
    }
  }, [renameFeed, showSuccessToast, showErrorToast, updateFeed]);

  const getFeedConnectionsHandler = useCallback(async (feedId: string, params?: { page?: number, limit?: number }) => {
    try {
      const result = await getFeedConnections(feedId, params);
      return result;
    } catch (error) {
      console.error("Error fetching feed connections:", error);
      showErrorToast({ message: "Failed to fetch feed connections" });
      return null;
    }
  }, [getFeedConnections, showErrorToast]);

  const bulkRemoveConnectionsFromFeedHandler = useCallback(async (feedId: string, connectionIds: string[]) => {
    try {
      const result = await bulkRemoveConnectionsFromFeed({ feedId, connectionIds });
      if(result?.success) {
        showSuccessToast({ message: result?.message });
        // Refresh feeds data to update numConnections and maxNumConnections
        await getFeedsHandler();
      } else {
        showErrorToast({ message: result?.message });
      }
      return result;
    } catch (error) {
      console.error("Error removing connections from feed:", error);
      showErrorToast({ message: "Failed to remove connections from feed" });
      return null;
    }
  }, [bulkRemoveConnectionsFromFeed, showSuccessToast, showErrorToast, getFeedsHandler]);

  return {
    getFeedsHandler,
    bulkAddConnectionsToFeedHandler,
    createFeedHandler,
    bulkRemoveConnectionsFromFeedHandler,
    deleteFeedHandler,
    renameFeedHandler,
    getFeedConnectionsHandler
  };
};
